import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { theme } from "../theme.js";

import InfoBox from "../components/base/SmallItem.jsx";
import {HeroReadOnly} from "../components/heroSection.jsx";
import AboutItem from "../components/aboutItem.jsx";
import Section from "../components/base/Section.jsx";
import PriceItem from "../components/priceItem.jsx";
import StickyColumn from "../components/StickyColumn.jsx";
import LoadingOverlay from "../components/LoadingOverlay.jsx";
import Button from "../components/base/Button.jsx";
import MapTrack from "../components/MapTrack.jsx";
import RaceProfile from "../components/RaceProfile.jsx";
import { getOrgIdFromStorage } from "../utils/storage.js";
import { API_URL } from "../utils/constants.js";
import { CalendarIcon, GlobeAltIcon, MapPinIcon, MegaphoneIcon, BuildingLibraryIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { icon } from "leaflet";

export default function RaceDetail({races}) {

  const loc = useLocation();
  const navigate = useNavigate();
  const {race_id } = useParams();

  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);  
  const [fetchedRace, setFetchedRace] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [trackData, setTrackData] = useState(null);
  const [organizer, setOrganizer] = useState(null);
  const [event, setEvent] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const raceFromNav = loc.state?.race;
  const orgFromNav = loc.state?.orgId 
  const orgId= getOrgIdFromStorage();
  const race =
    raceFromNav ||
    fetchedRace ||
    (races && races.find((r) => String(r.id_bdd || r.id) === String(race_id)));
 

  useEffect(() => {
    let isMounted = true;

    const fetchRaceIfNeeded = async () => {
      if (race) {
        return race;
      }
      // Fetch race from backend if not in props
      try {
        const res = await fetch(`${API_URL}/api/races/${race_id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (isMounted && data && !data.error) {
          console.log("[DEBUG] Fetched race:", data);
          setFetchedRace(data);
          return data;
        }
      } catch (err) {
        console.error("Error fetching race:", err);
        if (isMounted) setError("Race not found");
      }
      return null;
    };

    const fetchPrices = async (raceData) => {
      try {
        if (!raceData || (!raceData.id_bdd && !raceData.id)) {
          console.warn("[WARN] Cannot fetch prices: no valid race data");
          return;
        }
        const raceIdToUse = raceData.id_bdd || raceData.id;
        console.log("[DEBUG] Fetching prices for race_id:", raceIdToUse);
        const res = await fetch(`${API_URL}/api/prices?race_id=${raceIdToUse}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (isMounted) setPrices(data || []);
      } catch (err) {
        console.error("Error fetching prices:", err);
        if (isMounted) setError(err.message || "Error fetching prices");
      }
    };

    const fetchTrackData = async (raceData) => {
      if (raceData && raceData.gpx) {
        try {
          console.log("[DEBUG] Fetching track data for race.gpx:", raceData.gpx);
          const res = await fetch(`${API_URL}/api/tracks/${raceData.gpx}`);
          if (res.ok) {
            const data = await res.json();
            console.log("[DEBUG] Track data received:", data);
            if (isMounted) {
              setTrackData(data.geojson_simplified);
              console.log("[DEBUG] TrackData set to:", data.geojson_simplified);
            }
          } else {
            console.error("[ERROR] Track fetch failed:", res.status);
          }
        } catch (err) {
          console.error("Error fetching track data:", err);
        }
      } else {
        console.log("[DEBUG] No race.gpx found");
      }
    };

    const fetchOrganizer = async (raceData) => {
      try {
        console.log("[DEBUG] Fetching event for event_id:", raceData?.event_id);
        const res = await fetch(`${API_URL}/api/events`);
        if (res.ok) {
          const events = await res.json();
          const foundEvent = events.find(e => String(e.id_bdd) === String(raceData?.event_id));
          
          if (foundEvent) {
            if (isMounted) setEvent(foundEvent);
            
            // Also fetch the organizer using organizer_id
            if (foundEvent.organizer_id) {
              console.log("[DEBUG] Fetching organizer for organizer_id:", foundEvent.organizer_id);
              const orgRes = await fetch(`${API_URL}/api/organizers/${foundEvent.organizer_id}`);
              if (orgRes.ok) {
                const organizerData = await orgRes.json();
                console.log("[DEBUG] Organizer data fetched:", organizerData);
                if (isMounted) setOrganizer(organizerData);
              } else {
                console.error("[ERROR] Organizer fetch failed:", orgRes.status);
              }
            } else {
              console.warn("[WARN] Event has no organizer_id");
            }
          } else {
            console.warn("[WARN] Event not found");
          }
        } else {
          console.error("[ERROR] Events fetch failed:", res.status);
        }
      } catch (err) {
        console.error("Error fetching organizer:", err);
      }
    };

    const fetchAllData = async () => {
      const fetchedRace = await fetchRaceIfNeeded();
      if (!fetchedRace) {
        if (isMounted) setLoading(false);
        return;
      }
      await fetchPrices(fetchedRace);
      await fetchTrackData(fetchedRace);
      await fetchOrganizer(fetchedRace);
      if (isMounted) setLoading(false);
    };

    fetchAllData();

    return () => {
      isMounted = false;
    };
  }, [race_id]);

  if (error) return <p>Error: {error}</p>;
  if (!race) return <p>Race not found</p>;

  const title = race.name || "Untitled Race";
  const elev = race.elev_gain || "--";
  const date = race.date
    ? new Date(race.date).toLocaleDateString()
    : "Date TBD";
  const type = race.sport || "Race";
  const image = race.image || null;
  const distance = race.distance || "--";

  console.log(race);

  const handleModifyRace = () => {
    navigate(`/events/${race.event_id}/create-race`, { state: { editRace: race, orgId } });
  };

  const handlePublishRace = async () => {
    try {
      const res = await fetch(`${API_URL}/api/races/publish/${race.id_bdd}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: true }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updatedRace = await res.json();
      setFetchedRace(updatedRace);
      race.active = true; // Update local state to reflect published status
    } catch (err) {
      console.error("Error publishing race:", err);
      setError("Failed to publish race");
    }
  };

  const getLabelForKey = (key) => {
    const labelMap = {
      distance: "Distance",
      elevation_gain: "Elevation Gain",
      elevation_loss: "Elevation Loss",
      duration: "Duration",
      difficulty: "Difficulty",
      altitude: "Altitude",
      type: "Type",
    };
    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const formatValue = (key, value) => {
    if (!value && value !== 0) return "--";
    // Convert to number if it's a string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return value;
    // Check if value is already in km (less than 1000) for distance
    if (key.includes("distance")) {
      if (numValue < 1000) return `${numValue.toFixed(1)} km`;
      return `${(numValue / 1000).toFixed(1)} km`;
    }
    if (key.includes("elevation") || key.includes("altitude")) return `${Math.round(numValue)} m`;
    if (key.includes("duration")) return `${Math.round(numValue)} min`;
    return value;
  };

  const groupExtraInfo = () => {
    if (!race.extra_info) return {};
    const grouped = {};
    const entries = Object.entries(race.extra_info);
    
    // Check if any key has underscore
    const hasUnderscore = entries.some(([key]) => key.includes('_'));
    
    if (!hasUnderscore) {
      // Group everything together
      grouped['Details'] = entries.map(([key, value]) => ({
        key,
        itemKey: key.charAt(0).toUpperCase() + key.slice(1),
        value,
      }));
    } else {
      // Group by first part before underscore
      entries.forEach(([key, value]) => {
        const parts = key.split('_');
        const groupKey = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
        const itemKey = parts.slice(1).join('_') || key;
        if (!grouped[groupKey]) grouped[groupKey] = [];
        grouped[groupKey].push({ key, itemKey, value });
      });
    }
    return grouped;
  };

  // Shared summary content
  const summaryContent = (
    <>
      <div style={styles.summaryItem}>
        <InfoBox><CalendarIcon style={styles.icon} /> {date}</InfoBox>
      </div>
      <div style={styles.summaryItem}>
        <InfoBox>Type: {type}</InfoBox>
      </div>
      {Object.entries(groupExtraInfo()).map(([groupKey, items]) => (
        <div key={groupKey} style={styles.summaryGroup}>
          <div style={styles.groupTitle}>{groupKey}</div>
          {items.map(({ key, itemKey, value }) => (
            <div key={key} style={styles.groupItem}>
              <InfoBox>{itemKey.charAt(0).toUpperCase() + itemKey.slice(1)}: {formatValue(key, value)}</InfoBox>
            </div>
          ))}
        </div>
      ))}
      <div style={styles.organizerSeparator}>
        <div style={styles.groupTitle}>Organisation</div>
        <InfoBox><BuildingLibraryIcon style={styles.icon} /> {organizer?.name || organizer?.organization_name || "Chargement..."}</InfoBox>
        {event?.website && (
          <a href={event.website.startsWith('http') ? event.website : 'https://' + event.website} target="_blank" rel="noopener noreferrer" style={styles.websiteLink}>
            <GlobeAltIcon style={{ ...styles.icon, color: theme.colors.white }} /> Voir le site
          </a>
        )}
      </div>
      {orgId === orgFromNav && (
        <div style={styles.buttonsContainer}>
          {!race.active && (
            <Button
              icon={MegaphoneIcon}
              title="Publier la course"
              onClick={handlePublishRace}
            />
          )}
          <Button
            icon={PencilSquareIcon}
            title="Modifier la course"
            onClick={handleModifyRace}
          />
        </div>
      )}
    </>
  );

  return (
    <div style={styles.container}>
      <HeroReadOnly title={title} type={type} image={image} />
      {/* Mobile collapsible summary */}
      {isMobile && (
        <div style={styles.mobileSummary}>
          <button
            type="button"
            onClick={() => setSummaryOpen(o => !o)}
            style={styles.mobileSummaryToggle}
          >
            <span>Informations de la course</span>
            <span style={{ transition: "transform 0.2s", display: "inline-block", transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
          </button>
          {summaryOpen && (
            <div style={styles.mobileSummaryBody}>
              {summaryContent}
            </div>
          )}
        </div>
      )}
      {/* Main Content */}
      <div style={{ ...styles.content, gridTemplateColumns: isMobile ? "1fr" : "1fr 450px" }}>
        <div style={styles.mainColumn}>
          {/* Ticket Types */}
          <Section title="Billets">
              {[...prices].sort((a, b) => (a.price || 0) - (b.price || 0)).map((price) => (
                <PriceItem price={price} race={race}/>
              ))}
          </Section>

          <AboutItem description={race.description || "Aucune description disponible."} showFullDescription={showFullDescription} setShowFullDescription={setShowFullDescription} />

          {/* GPX Track Map */}
          {trackData && (
            <MapTrack trackData={trackData} title={`${race.name} - Parcours`} />
          )}
          {trackData && (
            <RaceProfile trackData={trackData} title={`${race.name} - Profil d'élévation`} />
          )}
          {race.gpx && !trackData && (
            <Section title="GPX Track">
              <div style={styles.locationBox}>
                <p style={styles.locationName}>Chargement de la carte...</p>
              </div>
            </Section>
          )}
          {!race.gpx && (
            <Section title="GPX Track">
              <div style={styles.locationBox}>
                <p style={styles.locationName}>Aucune trace GPX disponible</p>
              </div>
            </Section>
          )}
        </div>

        {/* Sticky Right Column — desktop only */}
        {!isMobile && (
          <StickyColumn title="Résumé de la course" topOffset="130px">
            {summaryContent}
          </StickyColumn>
        )}
      </div>
      <LoadingOverlay isVisible={loading} message="Chargement de la course..." />
    </div>
  );
}

const styles = {
  topMargin: {
    backgroundColor: theme.colors.bgDark,
    height: "80px",
    width: "100%",
  },
  container: {
    backgroundColor: "#ffffff",
    minHeight: "100vh",
  },
  mobileSummary: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 20px 10px",
  },
  mobileSummaryToggle: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.surface || "#fff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontSize: theme.fonts.size.md,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textInverted,
    marginTop: theme.spacing.lg,
  },
  mobileSummaryBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    border: `1px solid ${theme.colors.border}`,
    borderTop: "none",
    borderRadius: `0 0 ${theme.radius.md} ${theme.radius.md}`,
    backgroundColor: theme.colors.surface || "#fff",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 20px",
    display: "grid",
    gap: "40px",
  },
  mainColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "40px",
    minWidth: 0,
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },
  summaryGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
    paddingTop: theme.spacing.sm,
    borderTop: "1px solid #f0f0f0",
  },
  groupTitle: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.xs,
  },
  groupItem: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },
  organizerSeparator: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
    paddingTop: theme.spacing.lg,
    borderTop: "1px solid #e0e0e0",
    marginTop: theme.spacing.sm,
  },
  modifyButtonContainer: {
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.lg,
    borderTop: "1px solid #e0e0e0",
  },
  buttonsContainer: {
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.lg,
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
  },
  websiteLink: {
    display: "inline-block",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.primary || "#007bff",
    color: "white",
    textDecoration: "none",
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    textAlign: "center",
    transition: "background-color 0.2s ease",
  },
  label: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textMuted,
  },
  value: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textInverted,
    margin: "0",
  },
  icon: {
    width: "16px",
    height: "16px",
    color: theme.colors.textMuted,
    marginRight: "6px",
    verticalAlign: "middle",
  },
};
